export interface ScanPoint {
  x: number;
  y: number;
}

export interface SmartScanOptions {
  enhance?: boolean;
  maxDetectSize?: number;
}

export interface SmartScanResult {
  blob: Blob;
  method: 'perspective' | 'trim' | 'none';
  corners?: ScanPoint[];
}

const DEFAULT_MAX_DETECT = 960;

async function loadOrientedImage(blob: Blob): Promise<HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob, {
        imageOrientation: 'from-image' as ImageBitmapOptions['imageOrientation'],
      });
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('Could not read image'));
          img.src = canvas.toDataURL('image/jpeg', 0.95);
        });
      }
      bitmap.close();
    } catch {
      /* fall through */
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

function drawScaledGray(img: HTMLImageElement, maxSize: number) {
  const scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const gray = new Float32Array(w * h);
  for (let i = 0; i < w * h; i += 1) {
    const idx = i * 4;
    gray[i] = data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114;
  }
  return { gray, w, h, scale };
}

function blurGray(src: Float32Array, w: number, h: number): Float32Array {
  const out = new Float32Array(src.length);
  for (let y = 1; y < h - 1; y += 1) {
    for (let x = 1; x < w - 1; x += 1) {
      let sum = 0;
      for (let ky = -1; ky <= 1; ky += 1) {
        for (let kx = -1; kx <= 1; kx += 1) {
          sum += src[(y + ky) * w + (x + kx)];
        }
      }
      out[y * w + x] = sum / 9;
    }
  }
  return out;
}

function otsuThreshold(gray: Float32Array): number {
  const hist = new Array(256).fill(0);
  for (let i = 0; i < gray.length; i += 1) {
    hist[Math.min(255, Math.max(0, Math.round(gray[i])))] += 1;
  }
  const total = gray.length;
  let sum = 0;
  for (let i = 0; i < 256; i += 1) sum += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let max = 0;
  let threshold = 128;
  for (let t = 0; t < 256; t += 1) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) ** 2;
    if (between > max) {
      max = between;
      threshold = t;
    }
  }
  return threshold;
}

function dilateMask(mask: Uint8Array, w: number, h: number, radius = 2): Uint8Array {
  const out = new Uint8Array(mask.length);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let hit = 0;
      for (let dy = -radius; dy <= radius; dy += 1) {
        for (let dx = -radius; dx <= radius; dx += 1) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          if (mask[ny * w + nx]) hit = 1;
        }
      }
      out[y * w + x] = hit;
    }
  }
  return out;
}

function largestComponentBounds(mask: Uint8Array, w: number, h: number) {
  const visited = new Uint8Array(mask.length);
  let best = { size: 0, minX: 0, minY: 0, maxX: 0, maxY: 0, points: [] as ScanPoint[] };

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const start = y * w + x;
      if (!mask[start] || visited[start]) continue;
      const stack = [start];
      visited[start] = 1;
      let size = 0;
      let minX = x;
      let maxX = x;
      let minY = y;
      let maxY = y;
      const edgePoints: ScanPoint[] = [];
      while (stack.length) {
        const cur = stack.pop()!;
        const cy = Math.floor(cur / w);
        const cx = cur - cy * w;
        size += 1;
        minX = Math.min(minX, cx);
        maxX = Math.max(maxX, cx);
        minY = Math.min(minY, cy);
        maxY = Math.max(maxY, cy);

        const isEdge =
          cx === 0 || cy === 0 || cx === w - 1 || cy === h - 1
          || !mask[cur - 1] || !mask[cur + 1] || !mask[cur - w] || !mask[cur + w];
        if (isEdge) edgePoints.push({ x: cx, y: cy });

        if (cx > 0 && !visited[cur - 1] && mask[cur - 1]) {
          visited[cur - 1] = 1;
          stack.push(cur - 1);
        }
        if (cx < w - 1 && !visited[cur + 1] && mask[cur + 1]) {
          visited[cur + 1] = 1;
          stack.push(cur + 1);
        }
        if (cy > 0 && !visited[cur - w] && mask[cur - w]) {
          visited[cur - w] = 1;
          stack.push(cur - w);
        }
        if (cy < h - 1 && !visited[cur + w] && mask[cur + w]) {
          visited[cur + w] = 1;
          stack.push(cur + w);
        }
      }
      if (size > best.size) best = { size, minX, minY, maxX, maxY, points: edgePoints };
    }
  }
  return best;
}

function orderCorners(points: ScanPoint[]): ScanPoint[] {
  const sorted = [...points].sort((a, b) => a.y - b.y);
  const top = sorted.slice(0, 2).sort((a, b) => a.x - b.x);
  const bottom = sorted.slice(-2).sort((a, b) => a.x - b.x);
  return [top[0], top[1], bottom[1], bottom[0]];
}

function extremeHullCorners(points: ScanPoint[]): ScanPoint[] | null {
  if (points.length < 4) return null;
  let tl = points[0];
  let tr = points[0];
  let br = points[0];
  let bl = points[0];
  for (const p of points) {
    if (p.x + p.y < tl.x + tl.y) tl = p;
    if (p.x - p.y > tr.x - tr.y) tr = p;
    if (p.x + p.y > br.x + br.y) br = p;
    if (p.x - p.y < bl.x - bl.y) bl = p;
  }
  return orderCorners([tl, tr, br, bl]);
}

function boundsToCorners(minX: number, minY: number, maxX: number, maxY: number, padRatio = 0.008): ScanPoint[] {
  const padX = Math.max(1, Math.round((maxX - minX) * padRatio));
  const padY = Math.max(1, Math.round((maxY - minY) * padRatio));
  return orderCorners([
    { x: minX - padX, y: minY - padY },
    { x: maxX + padX, y: minY - padY },
    { x: maxX + padX, y: maxY + padY },
    { x: minX - padX, y: maxY + padY },
  ]);
}

function detectFromMask(mask: Uint8Array, w: number, h: number): ScanPoint[] | null {
  const closed = dilateMask(mask, w, h, 3);
  const { size, minX, minY, maxX, maxY, points } = largestComponentBounds(closed, w, h);
  const area = w * h;
  if (size < area * 0.03) return null;

  const boxW = maxX - minX;
  const boxH = maxY - minY;
  if (boxW < w * 0.12 || boxH < h * 0.12) return null;

  const hull = extremeHullCorners(points);
  if (hull) return hull;
  return boundsToCorners(minX, minY, maxX, maxY);
}

function detectDocumentQuad(gray: Float32Array, w: number, h: number): ScanPoint[] | null {
  const blurred = blurGray(gray, w, h);
  const threshold = otsuThreshold(blurred);

  const darkMask = new Uint8Array(w * h);
  const lightMask = new Uint8Array(w * h);
  for (let i = 0; i < blurred.length; i += 1) {
    darkMask[i] = blurred[i] < threshold ? 1 : 0;
    lightMask[i] = blurred[i] > threshold ? 1 : 0;
  }

  const dark = detectFromMask(darkMask, w, h);
  const light = detectFromMask(lightMask, w, h);

  if (dark && light) {
    const darkArea = quadArea(dark);
    const lightArea = quadArea(light);
    return darkArea <= lightArea ? dark : light;
  }
  return dark ?? light;
}

function detectTrimBounds(gray: Float32Array, w: number, h: number): ScanPoint[] | null {
  let sum = 0;
  for (let i = 0; i < gray.length; i += 1) sum += gray[i];
  const mean = sum / gray.length;

  const rowSum = new Float32Array(h);
  const colSum = new Float32Array(w);

  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const deviation = Math.abs(gray[y * w + x] - mean);
      rowSum[y] += deviation;
      colSum[x] += deviation;
    }
  }

  const rowThreshold = Math.max(w * 0.012, 3);
  const colThreshold = Math.max(h * 0.012, 3);

  let top = 0;
  let bottom = h - 1;
  let left = 0;
  let right = w - 1;

  while (top < bottom && rowSum[top] < rowThreshold) top += 1;
  while (bottom > top && rowSum[bottom] < rowThreshold) bottom -= 1;
  while (left < right && colSum[left] < colThreshold) left += 1;
  while (right > left && colSum[right] < colThreshold) right -= 1;

  if (bottom - top < h * 0.15 || right - left < w * 0.15) return null;
  if (top <= 1 && left <= 1 && bottom >= h - 2 && right >= w - 2) return null;

  return boundsToCorners(left, top, right, bottom, 0.01);
}

function quadArea(corners: ScanPoint[]): number {
  const [tl, tr, br, bl] = corners;
  const width = Math.max(Math.hypot(tl.x - tr.x, tl.y - tr.y), Math.hypot(bl.x - br.x, bl.y - br.y));
  const height = Math.max(Math.hypot(tl.x - bl.x, tl.y - bl.y), Math.hypot(tr.x - br.x, tr.y - br.y));
  return width * height;
}

function distance(a: ScanPoint, b: ScanPoint) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function outputSize(corners: ScanPoint[]) {
  const [tl, tr, br, bl] = corners;
  const width = Math.max(distance(tl, tr), distance(bl, br));
  const height = Math.max(distance(tl, bl), distance(tr, br));
  return {
    width: Math.max(1, Math.round(width)),
    height: Math.max(1, Math.round(height)),
  };
}

function clampCorners(corners: ScanPoint[], w: number, h: number): ScanPoint[] {
  return corners.map((p) => ({
    x: Math.max(0, Math.min(w - 1, p.x)),
    y: Math.max(0, Math.min(h - 1, p.y)),
  }));
}

function shouldApplyCrop(corners: ScanPoint[], imgW: number, imgH: number): boolean {
  const { width, height } = outputSize(corners);
  const croppedArea = width * height;
  const fullArea = imgW * imgH;
  if (croppedArea >= fullArea * 0.985) return false;
  if (width < imgW * 0.1 || height < imgH * 0.1) return false;
  return true;
}

function bilinearSample(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  x: number,
  y: number,
): [number, number, number] {
  const cx = Math.max(0, Math.min(w - 1.001, x));
  const cy = Math.max(0, Math.min(h - 1.001, y));
  const x0 = Math.floor(cx);
  const y0 = Math.floor(cy);
  const x1 = Math.min(w - 1, x0 + 1);
  const y1 = Math.min(h - 1, y0 + 1);
  const tx = cx - x0;
  const ty = cy - y0;
  const sample = (sx: number, sy: number) => {
    const idx = (sy * w + sx) * 4;
    return [data[idx], data[idx + 1], data[idx + 2]] as [number, number, number];
  };
  const c00 = sample(x0, y0);
  const c10 = sample(x1, y0);
  const c01 = sample(x0, y1);
  const c11 = sample(x1, y1);
  const mix = (a: number, b: number, t: number) => a + (b - a) * t;
  return [
    mix(mix(c00[0], c10[0], tx), mix(c01[0], c11[0], tx), ty),
    mix(mix(c00[1], c10[1], tx), mix(c01[1], c11[1], tx), ty),
    mix(mix(c00[2], c10[2], tx), mix(c01[2], c11[2], tx), ty),
  ];
}

function warpPerspective(
  img: HTMLImageElement,
  corners: ScanPoint[],
  outW: number,
  outH: number,
): HTMLCanvasElement {
  const srcCanvas = document.createElement('canvas');
  srcCanvas.width = img.naturalWidth;
  srcCanvas.height = img.naturalHeight;
  const srcCtx = srcCanvas.getContext('2d');
  if (!srcCtx) throw new Error('Canvas not supported');
  srcCtx.fillStyle = '#ffffff';
  srcCtx.fillRect(0, 0, srcCanvas.width, srcCanvas.height);
  srcCtx.drawImage(img, 0, 0);
  const src = srcCtx.getImageData(0, 0, srcCanvas.width, srcCanvas.height);

  const [tl, tr, br, bl] = corners;
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  const out = ctx.createImageData(outW, outH);

  for (let y = 0; y < outH; y += 1) {
    const v = outH <= 1 ? 0 : y / (outH - 1);
    for (let x = 0; x < outW; x += 1) {
      const u = outW <= 1 ? 0 : x / (outW - 1);
      const top = {
        x: tl.x + (tr.x - tl.x) * u,
        y: tl.y + (tr.y - tl.y) * u,
      };
      const bottom = {
        x: bl.x + (br.x - bl.x) * u,
        y: bl.y + (br.y - bl.y) * u,
      };
      const sx = top.x + (bottom.x - top.x) * v;
      const sy = top.y + (bottom.y - top.y) * v;
      const [r, g, b] = bilinearSample(src.data, srcCanvas.width, srcCanvas.height, sx, sy);
      const idx = (y * outW + x) * 4;
      out.data[idx] = r;
      out.data[idx + 1] = g;
      out.data[idx + 2] = b;
      out.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(out, 0, 0);
  return canvas;
}

function cropRect(img: HTMLImageElement, corners: ScanPoint[]): HTMLCanvasElement {
  const ordered = orderCorners(corners);
  const minX = Math.min(ordered[0].x, ordered[3].x);
  const maxX = Math.max(ordered[1].x, ordered[2].x);
  const minY = Math.min(ordered[0].y, ordered[1].y);
  const maxY = Math.max(ordered[2].y, ordered[3].y);
  const width = Math.max(1, Math.round(maxX - minX));
  const height = Math.max(1, Math.round(maxY - minY));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, minX, minY, width, height, 0, 0, width, height);
  return canvas;
}

function enhanceCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const copy = document.createElement('canvas');
  copy.width = canvas.width;
  copy.height = canvas.height;
  const copyCtx = copy.getContext('2d');
  if (!copyCtx) return canvas;
  copyCtx.filter = 'contrast(1.14) brightness(1.05) saturate(0.95)';
  copyCtx.drawImage(canvas, 0, 0);
  return copy;
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Scan export failed'))),
      'image/jpeg',
      quality,
    );
  });
}

function detectCorners(gray: Float32Array, w: number, h: number): { corners: ScanPoint[]; method: 'perspective' | 'trim' } | null {
  const quad = detectDocumentQuad(gray, w, h);
  if (quad) return { corners: quad, method: 'perspective' };

  const trim = detectTrimBounds(gray, w, h);
  if (trim) return { corners: trim, method: 'trim' };

  return null;
}

function isSkewed(corners: ScanPoint[]): boolean {
  const ordered = orderCorners(corners);
  const [tl, tr, br, bl] = ordered;
  const topAngle = Math.abs(Math.atan2(tr.y - tl.y, tr.x - tl.x));
  const leftAngle = Math.abs(Math.atan2(bl.y - tl.y, bl.x - tl.x));
  return topAngle > 0.04 || leftAngle > 0.04
    || Math.abs((tr.x - tl.x) - (br.x - bl.x)) > 8
    || Math.abs((bl.y - tl.y) - (br.y - tr.y)) > 8;
}

export async function smartScanDocument(
  blob: Blob,
  options: SmartScanOptions = {},
): Promise<SmartScanResult> {
  const isImage = blob.type.startsWith('image/')
    || (blob instanceof File && /\.(jpe?g|png|webp|heic|heif)$/i.test(blob.name));
  if (!isImage) {
    return { blob, method: 'none' };
  }

  const img = await loadOrientedImage(blob);
  const maxDetectSize = options.maxDetectSize ?? DEFAULT_MAX_DETECT;
  const { gray, w, h, scale } = drawScaledGray(img, maxDetectSize);
  const found = detectCorners(gray, w, h);

  if (!found) {
    return { blob, method: 'none' };
  }

  const fullCorners = clampCorners(
    found.corners.map((p) => ({ x: p.x / scale, y: p.y / scale })),
    img.naturalWidth,
    img.naturalHeight,
  );

  if (!shouldApplyCrop(fullCorners, img.naturalWidth, img.naturalHeight)) {
    return { blob, method: 'none' };
  }

  const usePerspective = found.method === 'perspective' && isSkewed(fullCorners);
  const { width, height } = outputSize(fullCorners);
  let canvas = usePerspective
    ? warpPerspective(img, fullCorners, width, height)
    : cropRect(img, fullCorners);

  if (options.enhance !== false) {
    canvas = enhanceCanvas(canvas);
  }

  const scanned = await canvasToJpeg(canvas);
  return {
    blob: scanned,
    method: usePerspective ? 'perspective' : 'trim',
    corners: fullCorners,
  };
}

export async function smartScanDocumentSafe(
  blob: Blob,
  options: SmartScanOptions = {},
): Promise<SmartScanResult> {
  try {
    return await smartScanDocument(blob, options);
  } catch {
    return { blob, method: 'none' };
  }
}

export function defaultDocumentCorners(width: number, height: number): ScanPoint[] {
  const insetX = Math.max(12, width * 0.06);
  const insetY = Math.max(12, height * 0.06);
  return orderCorners([
    { x: insetX, y: insetY },
    { x: width - insetX, y: insetY },
    { x: width - insetX, y: height - insetY },
    { x: insetX, y: height - insetY },
  ]);
}

/** Full image bounds — used when resetting manual crop to the entire page. */
export function fullDocumentCorners(width: number, height: number): ScanPoint[] {
  return orderCorners([
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ]);
}

export async function detectDocumentCornersOnly(blob: Blob): Promise<{
  corners: ScanPoint[];
  width: number;
  height: number;
} | null> {
  const isImage = blob.type.startsWith('image/')
    || (blob instanceof File && /\.(jpe?g|png|webp|heic|heif)$/i.test(blob.name));
  if (!isImage) return null;

  const img = await loadOrientedImage(blob);
  const { gray, w, h, scale } = drawScaledGray(img, DEFAULT_MAX_DETECT);
  const found = detectCorners(gray, w, h);

  const corners = found
    ? clampCorners(
      found.corners.map((p) => ({ x: p.x / scale, y: p.y / scale })),
      img.naturalWidth,
      img.naturalHeight,
    )
    : defaultDocumentCorners(img.naturalWidth, img.naturalHeight);

  return {
    corners,
    width: img.naturalWidth,
    height: img.naturalHeight,
  };
}

export async function cropDocumentWithCorners(
  blob: Blob,
  corners: ScanPoint[],
  options: { enhance?: boolean } = {},
): Promise<Blob> {
  const img = await loadOrientedImage(blob);
  const ordered = orderCorners(corners);
  const fullCorners = clampCorners(ordered, img.naturalWidth, img.naturalHeight);
  const usePerspective = isSkewed(fullCorners);
  const { width, height } = outputSize(fullCorners);
  let canvas = usePerspective
    ? warpPerspective(img, fullCorners, width, height)
    : cropRect(img, fullCorners);

  if (options.enhance !== false) {
    canvas = enhanceCanvas(canvas);
  }

  return canvasToJpeg(canvas);
}

export { detectCorners, detectTrimBounds, loadOrientedImage, orderCorners as orderDocumentCorners };
